import * as _ from 'lodash';
import React, { useCallback } from 'react';
import Select, { OptionTypeBase } from 'react-select';

import { IngredientGroup, Product } from '../../../interfaces';
import { useIntArraySearchParam, useIngredientGroups } from '../../../hooks';

interface Props {
  products: Product[];
  onSelect: { (groupId: number[]): void };
  onRemove: { (groupId: number): void };
  onClear: { (): void };
}

function ingredientGroupToOption(ig: IngredientGroup): OptionTypeBase {
  return {
    label: ig.priming.toLowerCase(),
    value: ig.groupId,
  };
}

export default function ActiveIngredientSelect(props: Props) {
  const { products, onSelect, onRemove, onClear } = props;
  const [excludeActiveIngredients] = useIntArraySearchParam(
    '-ingredientGroups'
  );
  const onChange = useCallback(
    (nextValue, { action, removedValue }) => {
      switch (action) {
        case 'select-option':
          onSelect(nextValue.map(({ value }: { value: number }) => value));
          break;
        case 'remove-value':
        case 'pop-value':
          onRemove(removedValue.value);
          break;
        case 'clear':
          onClear();
          break;
      }
    },
    [onSelect, onRemove, onClear]
  );
  const igs = useIngredientGroups();

  const igLookup = _.keyBy(igs, 'groupId');
  const groupIdSet = (products ?? [])
    .map(({ ingredients }) => ingredients.map(({ groupId }) => groupId))
    .reduce((acc, groupIds) => {
      groupIds.forEach((groupId) => acc.add(groupId));
      return acc;
    }, new Set<number>());
  const options = Array.from(groupIdSet)
    .filter((groupId) => igLookup[groupId])
    .map((groupId) => ingredientGroupToOption(igLookup[groupId]));
  const currentOptions = excludeActiveIngredients
    .filter((groupId) => igLookup[groupId])
    .map((groupId) => ingredientGroupToOption(igLookup[groupId]));

  const allOptions = currentOptions
    .concat(options)
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Select
      onChange={onChange}
      options={allOptions}
      value={currentOptions}
      isMulti
      isSearchable
      isClearable
    />
  );
}
